import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, User, Phone, Mail, MapPin, Clock, Calendar,
  FileDown, X, Save, MessageCircle, History, Filter, AlertTriangle,
  Building2, RefreshCw, ChevronDown, LogOut, CheckCircle2, Eye, Settings
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { AccionesPanel, NotasPanel } from './DerivacionSeguimiento';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const ESTADOS = [
  { value: 'Nueva', label: 'Nueva', color: 'bg-red-100 text-red-700' },
  { value: 'Contactada', label: 'Contactada', color: 'bg-blue-100 text-blue-700' },
  { value: 'Sin respuesta', label: 'Sin respuesta', color: 'bg-orange-100 text-orange-700' },
  { value: 'Cita agendada', label: 'Cita agendada', color: 'bg-purple-100 text-purple-700' },
  { value: 'Consulta realizada', label: 'Consulta realizada', color: 'bg-teal-100 text-teal-700' },
  { value: 'Presupuesto enviado', label: 'Presupuesto enviado', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'Cirugía programada', label: 'Cirugía programada', color: 'bg-green-100 text-green-700' },
  { value: 'No interesada', label: 'No interesada', color: 'bg-gray-100 text-gray-500' },
];

const getEstadoBadge = (estado) => {
  const e = ESTADOS.find(s => s.value === estado) || { color: 'bg-gray-100 text-gray-500' };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${e.color}`}>{estado}</span>;
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
};
const formatDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

// ─── New Derivation Form ───
const DerivacionForm = ({ catalogo, onClose, onCreated, token }) => {
  const [form, setForm] = useState({
    nombre: '', apellidos: '', telefono: '', email: '',
    localidad: '', provincia: '', motivo_consulta: '', motivo_texto_libre: '',
    franja_horaria: '', canal_preferido: '', observaciones_clinica: '', consentimiento: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const allServicios = useMemo(() => {
    const list = [];
    (catalogo || []).forEach(c => {
      if (c.categoria === 'Otros') return;
      if (c.servicios?.length > 0) {
        list.push({ label: `— ${c.categoria} —`, value: '', disabled: true });
        c.servicios.forEach(s => list.push({ label: s, value: s }));
      }
    });
    list.push({ label: '— Otros —', value: '', disabled: true });
    list.push({ label: 'Otros (describir)', value: 'Otros' });
    return list;
  }, [catalogo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.apellidos || !form.telefono || !form.motivo_consulta || !form.franja_horaria || !form.canal_preferido) {
      setError('Rellene todos los campos obligatorios (*)'); return;
    }
    if (form.motivo_consulta === 'Otros' && !form.motivo_texto_libre.trim()) {
      setError('Cuando el motivo es "Otros", debe describir el motivo'); return;
    }
    if (!form.consentimiento) { setError('Debe confirmar el consentimiento del paciente'); return; }
    const phoneClean = form.telefono.replace(/\s/g, '');
    if (!/^\+?\d{9,15}$/.test(phoneClean)) { setError('El teléfono debe tener entre 9 y 15 dígitos'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/api/derivaciones`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...form, telefono: phoneClean })
      });
      if (res.ok) { onCreated(); onClose(); }
      else { const d = await res.json(); setError(d.detail || 'Error al crear la derivación'); }
    } catch { setError('Error de conexión'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="derivacion-modal">
      <Card className="w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-[#1C1C1A]">Nueva Derivación</h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Nombre *</label>
                <input required value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="deriv-nombre" /></div>
              <div><label className="block text-sm font-medium mb-1">Apellidos *</label>
                <input required value={form.apellidos} onChange={e => setForm({...form, apellidos: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="deriv-apellidos" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Teléfono * <span className="text-xs text-gray-400">(9-15 dígitos)</span></label>
                <input required type="tel" value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="deriv-telefono" /></div>
              <div><label className="block text-sm font-medium mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Localidad</label>
                <input value={form.localidad} onChange={e => setForm({...form, localidad: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
              <div><label className="block text-sm font-medium mb-1">Provincia</label>
                <input value={form.provincia} onChange={e => setForm({...form, provincia: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Ej: Málaga" /></div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Motivo de consulta *</label>
              <select required value={form.motivo_consulta} onChange={e => setForm({...form, motivo_consulta: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="deriv-motivo">
                <option value="">Seleccionar...</option>
                {allServicios.map((s, i) => <option key={i} value={s.value} disabled={s.disabled}>{s.label}</option>)}
              </select>
            </div>
            {form.motivo_consulta === 'Otros' && (
              <div><label className="block text-sm font-medium mb-1">Describa el motivo *</label>
                <input required value={form.motivo_texto_libre} onChange={e => setForm({...form, motivo_texto_libre: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Texto libre..." data-testid="deriv-motivo-libre" /></div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Franja horaria *</label>
                <select required value={form.franja_horaria} onChange={e => setForm({...form, franja_horaria: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="deriv-franja">
                  <option value="">Seleccionar...</option>
                  <option value="Mañana">Mañana</option>
                  <option value="Tarde">Tarde</option>
                  <option value="Indiferente">Indiferente</option>
                </select></div>
              <div><label className="block text-sm font-medium mb-1">Canal preferido *</label>
                <select required value={form.canal_preferido} onChange={e => setForm({...form, canal_preferido: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="deriv-canal">
                  <option value="">Seleccionar...</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Llamada telefónica">Llamada telefónica</option>
                </select></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Observaciones de la clínica</label>
              <textarea value={form.observaciones_clinica} onChange={e => setForm({...form, observaciones_clinica: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" rows={3} /></div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.consentimiento} onChange={e => setForm({...form, consentimiento: e.target.checked})} className="mt-0.5" data-testid="deriv-consentimiento" />
                <span className="text-sm text-amber-900">
                  Confirmo que el paciente ha dado su consentimiento verbal para compartir sus datos personales y de salud con el Dr. Ángel Norro, con el fin de valorar su caso y contactarle. Se le ha informado de que podrá ejercer sus derechos de acceso, rectificación y supresión.
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-[#B8975A] hover:bg-[#9A7D4A] text-white" disabled={saving} data-testid="deriv-submit">
              <Save size={16} className="mr-2" />{saving ? 'Guardando...' : 'Enviar Derivación'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// ─── Detail Panel ───
const DerivacionDetalle = ({ derivacion, isAdmin, token, onUpdated }) => {
  const [editEstado, setEditEstado] = useState(derivacion.estado);
  const [fechaCita, setFechaCita] = useState(derivacion.fecha_cita || '');
  const [modalidadCita, setModalidadCita] = useState(derivacion.modalidad_cita || '');
  const [notasInternas, setNotasInternas] = useState(derivacion.notas_internas || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditEstado(derivacion.estado);
    setFechaCita(derivacion.fecha_cita || '');
    setModalidadCita(derivacion.modalidad_cita || '');
    setNotasInternas(derivacion.notas_internas || '');
  }, [derivacion]);

  const saveUpdate = async (data) => {
    setSaving(true);
    try {
      await fetch(`${API}/api/derivaciones/${derivacion.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      onUpdated();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const diasClass = derivacion.estado === 'Nueva' && derivacion.dias_transcurridos > 7
    ? 'text-red-600 font-bold' : 'text-gray-500';

  return (
    <div className="p-6 space-y-6" data-testid="derivacion-detalle">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-light text-[#1C1C1A]">{derivacion.nombre} {derivacion.apellidos}</h2>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {getEstadoBadge(derivacion.estado)}
            <span className={`text-sm flex items-center gap-1 ${diasClass}`}>
              <Clock size={14} /> {derivacion.dias_transcurridos} días
            </span>
            {derivacion.clinica_nombre && isAdmin && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                <Building2 size={12} className="inline mr-1" />{derivacion.clinica_nombre}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => window.open(`tel:${derivacion.telefono}`, '_self')} className="bg-[#B8975A] hover:bg-[#9A7D4A] text-white">
            <Phone size={14} className="mr-1" />Llamar
          </Button>
          <Button size="sm" onClick={() => window.open(`https://wa.me/${derivacion.telefono?.replace(/[^0-9]/g, '')}`, '_blank')} className="bg-green-500 hover:bg-green-600 text-white">
            <MessageCircle size={14} className="mr-1" />WhatsApp
          </Button>
        </div>
      </div>

      {/* Contact + Location */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#FDFBF8] p-4 rounded-lg space-y-2">
          <h3 className="text-xs tracking-wider uppercase text-gray-400 mb-2">Datos de contacto</h3>
          <div className="flex items-center gap-2 text-sm"><Phone size={14} className="text-[#B8975A]" />{derivacion.telefono}</div>
          {derivacion.email && <div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-[#B8975A]" />{derivacion.email}</div>}
          <div className="flex items-center gap-2 text-sm"><Clock size={14} className="text-[#B8975A]" />Franja: {derivacion.franja_horaria}</div>
          <div className="flex items-center gap-2 text-sm"><MessageCircle size={14} className="text-[#B8975A]" />Canal: {derivacion.canal_preferido}</div>
        </div>
        <div className="bg-[#FDFBF8] p-4 rounded-lg space-y-2">
          <h3 className="text-xs tracking-wider uppercase text-gray-400 mb-2">Ubicación y fechas</h3>
          {(derivacion.localidad || derivacion.provincia) && <div className="flex items-center gap-2 text-sm"><MapPin size={14} className="text-[#B8975A]" />{[derivacion.localidad, derivacion.provincia].filter(Boolean).join(', ')}</div>}
          <div className="text-sm"><span className="text-gray-400">Derivación:</span> {formatDate(derivacion.fecha_derivacion)}</div>
          {derivacion.fecha_primer_contacto && <div className="text-sm"><span className="text-gray-400">1er contacto:</span> {formatDate(derivacion.fecha_primer_contacto)}</div>}
          {derivacion.fecha_cita && <div className="text-sm"><span className="text-gray-400">Cita:</span> {derivacion.fecha_cita} {derivacion.modalidad_cita && `(${derivacion.modalidad_cita})`}</div>}
        </div>
      </div>

      {/* Motivo */}
      <div className="bg-[#FDFBF8] p-4 rounded-lg">
        <h3 className="text-xs tracking-wider uppercase text-gray-400 mb-2">Motivo de consulta</h3>
        <p className="text-sm text-[#1C1C1A] font-medium">{derivacion.motivo_consulta}</p>
        {derivacion.motivo_texto_libre && <p className="text-sm text-gray-500 mt-1">{derivacion.motivo_texto_libre}</p>}
      </div>

      {/* Observaciones de la clínica */}
      {derivacion.observaciones_clinica && (
        <div className="bg-[#FDFBF8] p-4 rounded-lg">
          <h3 className="text-xs tracking-wider uppercase text-gray-400 mb-2">Observaciones de la clínica</h3>
          <p className="text-sm text-gray-600">{derivacion.observaciones_clinica}</p>
        </div>
      )}

      {/* Admin: State change + Appointment + Private notes */}
      {isAdmin && (
        <>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="text-xs tracking-wider uppercase text-gray-400 mb-3">Cambiar estado</h3>
            <div className="flex flex-wrap gap-2">
              {ESTADOS.map(est => (
                <button key={est.value} onClick={() => { setEditEstado(est.value); saveUpdate({ estado: est.value }); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${derivacion.estado === est.value ? est.color + ' ring-2 ring-offset-1 ring-gray-400' : 'bg-white border border-gray-200 text-gray-500 hover:border-gray-400'}`}
                  data-testid={`estado-btn-${est.value}`} disabled={saving}>
                  {est.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="text-xs tracking-wider uppercase text-gray-400 mb-3">Cita</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1">Fecha y hora de cita</label>
                <input type="datetime-local" value={fechaCita} onChange={e => setFechaCita(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm mb-1">Modalidad</label>
                <select value={modalidadCita} onChange={e => setModalidadCita(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
                  <option value="">Seleccionar...</option>
                  <option value="Presencial">Presencial</option>
                  <option value="Videollamada">Videollamada</option>
                  <option value="Telefónica">Telefónica</option>
                </select>
              </div>
            </div>
            <Button size="sm" className="mt-3 bg-[#B8975A] hover:bg-[#9A7D4A] text-white" disabled={saving}
              onClick={() => saveUpdate({ fecha_cita: fechaCita, modalidad_cita: modalidadCita, estado: 'Cita agendada' })}>
              <Calendar size={14} className="mr-1" />Guardar cita
            </Button>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200" data-testid="notas-privadas-doctor">
            <h3 className="text-xs tracking-wider uppercase text-amber-700 mb-3 flex items-center gap-2">
              <Eye size={14} />Notas privadas del doctor <span className="text-[10px] bg-amber-200 px-1.5 py-0.5 rounded">Solo visible para el doctor</span>
            </h3>
            <textarea value={notasInternas} onChange={e => setNotasInternas(e.target.value)}
              className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white" rows={4}
              placeholder="Anotaciones internas, valoración clínica..." />
            <Button size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white" disabled={saving}
              onClick={() => saveUpdate({ notas_internas: notasInternas })}>
              <Save size={14} className="mr-1" />Guardar notas
            </Button>
          </div>
        </>
      )}

      <AccionesPanel derivacion={derivacion} token={token} onUpdated={onUpdated} isAdmin={isAdmin} />
      <NotasPanel derivacion={derivacion} token={token} onUpdated={onUpdated} />

      {/* Historial */}
      <div>
        <h3 className="text-xs tracking-wider uppercase text-gray-400 mb-3 flex items-center gap-2"><History size={14} />Historial</h3>
        {derivacion.historial?.length > 0 ? (
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {derivacion.historial.map((h, i) => (
              <div key={i} className="bg-[#FDFBF8] p-3 rounded-lg border-l-3 border-l-[#B8975A] text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-[#1C1C1A]">{h.accion}</span>
                  <span className="text-xs text-gray-400">{formatDateTime(h.fecha)}</span>
                </div>
                <span className="text-xs text-gray-400">Por: {h.usuario}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 bg-[#FDFBF8] p-4 rounded-lg text-center">Sin historial</p>
        )}
      </div>
    </div>
  );
};

// ─── Profile Modal ───
const ProfileModal = ({ token, user, onClose, onUpdated }) => {
  const [profile, setProfile] = useState({ email: '', contacto: '', telefono: '', direccion: '', ciudad: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/mi-perfil`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setProfile({ email: data.email || '', contacto: data.contacto || '', telefono: data.telefono || '', direccion: data.direccion || '', ciudad: data.ciudad || '' });
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, [token]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!profile.email.trim()) { setError('El email es obligatorio.'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API}/api/auth/mi-perfil`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(profile)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al guardar');
      setSuccess(`Perfil guardado. Ya puede entrar con "${data.username}" o con ${data.email}.`);
      onUpdated && onUpdated();
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="profile-modal">
      <Card className="w-full max-w-lg">
        <form onSubmit={handleSave} className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-[#1C1C1A]">Mi perfil — {user?.nombre_clinica}</h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
          {success && <div className="mb-4 p-3 bg-green-50 text-green-700 text-sm rounded-lg">{success}</div>}

          {loading ? <div className="text-center py-8 text-gray-400">Cargando...</div> : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email * <span className="text-xs text-gray-400">(también servirá para iniciar sesión)</span></label>
                <input type="email" required value={profile.email} onChange={e => setProfile({...profile, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="clinica@ejemplo.com" data-testid="profile-email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Persona de contacto</label>
                <input value={profile.contacto} onChange={e => setProfile({...profile, contacto: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Nombre del responsable" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Teléfono</label>
                  <input type="tel" value={profile.telefono} onChange={e => setProfile({...profile, telefono: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ciudad</label>
                  <input value={profile.ciudad} onChange={e => setProfile({...profile, ciudad: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Dirección</label>
                <input value={profile.direccion} onChange={e => setProfile({...profile, direccion: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="outline" onClick={onClose}>Cerrar</Button>
            <Button type="submit" className="bg-[#B8975A] hover:bg-[#9A7D4A] text-white" disabled={saving || loading} data-testid="profile-save">
              <Save size={16} className="mr-2" />{saving ? 'Guardando...' : 'Guardar perfil'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};



// ─── Main Portal ───
const ClinicaPortal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState('');
  const [derivaciones, setDerivaciones] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterClinica, setFilterClinica] = useState('');
  const [filterMotivo, setFilterMotivo] = useState('');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const isAdmin = user?.rol === 'admin';

  useEffect(() => {
    const t = localStorage.getItem('clinica_token');
    const u = localStorage.getItem('clinica_user');
    if (!t || !u) { navigate('/clinica/login'); return; }
    setToken(t);
    try { setUser(JSON.parse(u)); } catch { navigate('/clinica/login'); }
    // Disable PostHog tracking on CRM routes
    if (window.posthog) {
      window.posthog.opt_out_capturing();
      window.posthog.stopSessionRecording && window.posthog.stopSessionRecording();
    }
    return () => {
      if (window.posthog) window.posthog.opt_in_capturing();
    };
  }, [navigate]);

  const fetchDerivaciones = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/derivaciones`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setDerivaciones(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token]);

  const fetchDetalle = useCallback(async (id) => {
    if (!token || !id) return;
    try {
      const res = await fetch(`${API}/api/derivaciones/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setDetalle(await res.json());
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchCatalogo = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/catalogo-servicios`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setCatalogo(await res.json());
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => { fetchDerivaciones(); fetchCatalogo(); }, [fetchDerivaciones, fetchCatalogo]);

  useEffect(() => { if (selectedId) fetchDetalle(selectedId); }, [selectedId, fetchDetalle]);

  const handleExportCSV = async () => {
    const res = await fetch(`${API}/api/export/derivaciones-csv`, { headers: { 'Authorization': `Bearer ${token}` } });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'derivaciones.csv'; a.click();
    }
  };

  const clinicaNames = useMemo(() => {
    const names = new Set();
    derivaciones.forEach(d => { if (d.clinica_nombre) names.add(d.clinica_nombre); });
    return Array.from(names).sort();
  }, [derivaciones]);

  const motivos = useMemo(() => {
    const m = new Set();
    derivaciones.forEach(d => { if (d.motivo_consulta) m.add(d.motivo_consulta); });
    return Array.from(m).sort();
  }, [derivaciones]);

  const filtered = useMemo(() => {
    return derivaciones.filter(d => {
      if (filterEstado && d.estado !== filterEstado) return false;
      if (filterClinica && d.clinica_nombre !== filterClinica) return false;
      if (filterMotivo && d.motivo_consulta !== filterMotivo) return false;
      if (filterFechaDesde) {
        const fd = d.fecha_derivacion?.slice(0, 10) || '';
        if (fd < filterFechaDesde) return false;
      }
      if (filterFechaHasta) {
        const fd = d.fecha_derivacion?.slice(0, 10) || '';
        if (fd > filterFechaHasta) return false;
      }
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const full = `${d.nombre} ${d.apellidos} ${d.telefono} ${d.email}`.toLowerCase();
        if (!full.includes(s)) return false;
      }
      return true;
    });
  }, [derivaciones, filterEstado, filterClinica, filterMotivo, filterFechaDesde, filterFechaHasta, searchTerm]);

  const stats = useMemo(() => ({
    total: derivaciones.length,
    nuevas: derivaciones.filter(d => d.estado === 'Nueva').length,
    urgentes: derivaciones.filter(d => d.estado === 'Nueva' && d.dias_transcurridos > 7).length,
  }), [derivaciones]);

  const logout = () => {
    localStorage.removeItem('clinica_token');
    localStorage.removeItem('clinica_user');
    navigate('/clinica/login');
  };

  // Session timeout after 15 minutes of inactivity
  useEffect(() => {
    const TIMEOUT_MS = 15 * 60 * 1000;
    let timer = setTimeout(logout, TIMEOUT_MS);
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(logout, TIMEOUT_MS);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F0EDE7]" data-testid="clinica-portal">
      {/* Top bar */}
      <div className="bg-[#1C1C1A] text-white px-6 py-3 flex items-center justify-between">
        <div>
          <span className="text-[#B8975A] font-semibold">Dr. Ángel Norro</span>
          <span className="text-gray-400 mx-2">·</span>
          <span className="text-sm text-gray-300">{user.nombre_clinica}</span>
          {isAdmin && <span className="ml-2 text-[10px] bg-[#B8975A] text-white px-2 py-0.5 rounded-full">ADMIN</span>}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowProfile(true)} className="text-gray-400 hover:text-white flex items-center gap-1 text-sm" data-testid="profile-btn">
            <Settings size={14} />Mi perfil
          </button>
          <button onClick={logout} className="text-gray-400 hover:text-white flex items-center gap-1 text-sm">
            <LogOut size={14} />Salir
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-[#1C1C1A]">{stats.total}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">Total derivaciones</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.nuevas}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">Nuevas</div>
          </Card>
          <Card className="p-4 text-center">
            <div className={`text-2xl font-bold ${stats.urgentes > 0 ? 'text-red-600' : 'text-green-600'}`}>{stats.urgentes}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wider flex items-center justify-center gap-1">
              {stats.urgentes > 0 && <AlertTriangle size={12} />}Urgentes (&gt;7 días)
            </div>
          </Card>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar por nombre, teléfono o email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="search-input" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1">
            <Filter size={14} />Filtros {(filterEstado || filterClinica || filterMotivo || filterFechaDesde || filterFechaHasta) && <span className="bg-[#B8975A] text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">!</span>}
          </Button>
          <Button size="sm" onClick={handleExportCSV} variant="outline" className="gap-1" data-testid="export-csv-btn">
            <FileDown size={14} />CSV
          </Button>
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-[#B8975A] hover:bg-[#9A7D4A] text-white gap-1" data-testid="new-derivacion-btn">
            <Plus size={14} />Nueva derivación
          </Button>
        </div>

        {/* Filters row */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white rounded-lg border border-gray-200">
            <select value={filterEstado} onChange={e => setFilterEstado(e.target.value)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white">
              <option value="">Todos los estados</option>
              {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
            <select value={filterMotivo} onChange={e => setFilterMotivo(e.target.value)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white">
              <option value="">Todos los motivos</option>
              {motivos.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {isAdmin && (
              <select value={filterClinica} onChange={e => setFilterClinica(e.target.value)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white">
                <option value="">Todas las clínicas</option>
                {clinicaNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400">Desde</span>
              <input type="date" value={filterFechaDesde} onChange={e => setFilterFechaDesde(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white" />
              <span className="text-xs text-gray-400">Hasta</span>
              <input type="date" value={filterFechaHasta} onChange={e => setFilterFechaHasta(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white" />
            </div>
            {(filterEstado || filterClinica || filterMotivo || filterFechaDesde || filterFechaHasta) && (
              <button onClick={() => { setFilterEstado(''); setFilterClinica(''); setFilterMotivo(''); setFilterFechaDesde(''); setFilterFechaHasta(''); }} className="text-xs text-red-500 hover:text-red-700">Limpiar filtros</button>
            )}
          </div>
        )}

        {/* Main content: Table + Detail */}
        {selectedId && detalle ? (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
            <div className="lg:col-span-5">
              <Card>
                <div className="flex items-center justify-between p-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-[#1C1C1A]">Detalle de derivación</span>
                  <button onClick={() => { setSelectedId(null); setDetalle(null); }} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>
                <DerivacionDetalle derivacion={detalle} isAdmin={isAdmin} token={token}
                  onUpdated={() => { fetchDetalle(selectedId); fetchDerivaciones(); }} />
              </Card>
            </div>
          </div>
        ) : null}

        {/* Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-400"><RefreshCw className="animate-spin mx-auto mb-2" size={24} />Cargando...</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <User className="mx-auto mb-2 text-gray-300" size={36} />
                <p className="text-sm">{derivaciones.length === 0 ? 'No hay derivaciones aún' : 'Sin resultados para el filtro actual'}</p>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="derivaciones-table">
                <thead>
                  <tr className="bg-[#FDFBF8] border-b border-gray-200 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Fecha</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Paciente</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Teléfono</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Motivo</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider text-center">Días</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Estado</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Fecha cita</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider text-center">Pendientes</th>
                    {isAdmin && <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Clínica</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(d => {
                    const isUrgent = d.estado === 'Nueva' && d.dias_transcurridos > 7;
                    return (
                      <tr key={d.id} onClick={() => { setSelectedId(d.id); setDetalle(null); }}
                        className={`border-b border-gray-50 cursor-pointer hover:bg-[#FDFBF8] transition-colors ${selectedId === d.id ? 'bg-[#FDFBF8]' : ''} ${isUrgent ? 'bg-red-50 hover:bg-red-100' : ''}`}
                        data-testid={`derivacion-row-${d.id}`}>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(d.fecha_derivacion)}</td>
                        <td className="px-4 py-3 font-medium text-[#1C1C1A] whitespace-nowrap">{d.nombre} {d.apellidos}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{d.telefono}</td>
                        <td className="px-4 py-3 text-gray-500 max-w-[180px] truncate">{d.motivo_consulta}</td>
                        <td className={`px-4 py-3 text-center font-mono ${isUrgent ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                          {d.dias_transcurridos}
                          {isUrgent && <AlertTriangle size={12} className="inline ml-1" />}
                        </td>
                        <td className="px-4 py-3">{getEstadoBadge(d.estado)}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{d.fecha_cita || '—'}</td>
                        <td className="px-4 py-3 text-center" data-testid={`pendientes-${d.id}`}>
                          {d.acciones_pendientes > 0 ? <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">{d.acciones_pendientes}</span> : <span className="text-gray-300">—</span>}
                        </td>
                        {isAdmin && <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{d.clinica_nombre}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>

      {/* New derivation modal */}
      {showForm && (
        <DerivacionForm catalogo={catalogo} token={token} onClose={() => setShowForm(false)}
          onCreated={() => { fetchDerivaciones(); }} />
      )}
      {showProfile && (
        <ProfileModal token={token} user={user} onClose={() => setShowProfile(false)} />
      )}
    </div>
  );
};

export default ClinicaPortal;
